import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { ImageSelector } from '@/components/media/image-selector';

interface TimerFormProps {
  timer?: {
    id: number | string;
    name: string;
    durationSeconds: number;
    image: string | null;
    completionSound?: string | null;
    completionResourceType?: string | null;
    completionResourceId?: string | null;
    segments?: Array<{
      id?: string | number;
      name?: string;
      label?: string;
      durationSeconds?: number;
      duration?: number;
      image?: string | null;
      content?: string;
    }>;
  };
  onSave: (timerData: {
    name: string;
    durationSeconds: number;
    image: string | null;
    completionSound?: string | null;
    completionResourceType?: string | null;
    completionResourceId?: string | null;
  }) => void;
  onCancel: () => void;
  isOpen: boolean;
  timerType: 'circular' | 'rectangular';
  hubId?: string;
  availableResources?: Array<{ id: string; title: string; type: 'schedule' | 'narrative' }>;
}

export default function TimerForm({ timer, onSave, onCancel, isOpen, timerType, hubId, availableResources }: TimerFormProps) {
  // Debug logging
  console.log('🟦 TimerForm rendered with timerType:', timerType);
  console.log('🟦 TimerForm isOpen:', isOpen);
  
  const [title, setTitle] = useState(timer?.name || '');
  const [minutes, setMinutes] = useState(Math.floor((timer?.durationSeconds || 300) / 60));
  const [seconds, setSeconds] = useState((timer?.durationSeconds || 300) % 60);
  const { toast } = useToast();
  const [imageUrl, setImageUrl] = useState(timer?.image || '');
  // Holds the raw File when the user picks a local upload — cleared after successful upload.
  // The base64 preview is stored in imageUrl for display only; the actual file is uploaded
  // separately before save to avoid sending a large base64 blob in the timer JSON body
  // (which triggers GCP Cloud Armor WAF body-size rules and returns 403).
  const pendingImageFile = useRef<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [mode, setMode] = useState<'cover' | 'uncover'>('cover');
  const [timerMode, setTimerMode] = useState<'duration' | 'target'>('duration');
  const [targetTime, setTargetTime] = useState('');
  const [sections, setSections] = useState<Array<{
    id: string;
    title: string;
    duration: number;
    image: string;
  }>>([]);

  const [completionSound, setCompletionSound] = useState(timer?.completionSound ?? 'none');
  const [completionResourceType, setCompletionResourceType] = useState<string>(timer?.completionResourceType || '');
  const [completionResourceId, setCompletionResourceId] = useState<string>(timer?.completionResourceId || '');

  // Hydrate form state when timer prop changes (for editing)
  useEffect(() => {
    if (timer && isOpen) {
      console.log('🟦 TimerForm hydrating with timer data:', timer);
      setTitle(timer.name || '');
      const totalSeconds = timer.durationSeconds || 300;
      setMinutes(Math.floor(totalSeconds / 60));
      setSeconds(totalSeconds % 60);
      setImageUrl(timer.image || '');
      pendingImageFile.current = null;
      setTimerMode('duration');
      setSections((timer.segments ?? []).map((section, index) => ({
        id: String(section.id ?? index),
        title: section.name || section.label || `Step ${index + 1}`,
        duration: Math.max(0, Number(section.durationSeconds ?? section.duration ?? 0)),
        image: section.image || section.content || '',
      })));
      setCompletionSound(timer.completionSound ?? 'none');
      setCompletionResourceType(timer.completionResourceType || '');
      setCompletionResourceId(timer.completionResourceId || '');
    } else if (!timer && isOpen) {
      console.log('🟦 TimerForm reset for create mode');
      setTitle('');
      setMinutes(5);
      setSeconds(0);
      setImageUrl('');
      pendingImageFile.current = null;
      setTimerMode('duration');
      setTargetTime('');
       setSections([]);
      setCompletionSound('none');
      setCompletionResourceType('');
      setCompletionResourceId('');
    }
  }, [timer, isOpen]);

  const calculateDurationFromTarget = () => {
    if (!targetTime) return 0;
    
    const now = new Date();
    const target = new Date();
    const [hours, minutes] = targetTime.split(':').map(Number);
    
    target.setHours(hours, minutes, 0, 0);
    
    // If target time is before current time, assume it's for tomorrow
    if (target <= now) {
      target.setDate(target.getDate() + 1);
    }
    
    return Math.floor((target.getTime() - now.getTime()) / 1000);
  };

  const addSection = () => {
    const newSection = {
      id: Date.now().toString(),
      title: `Step ${sections.length + 1}`,
      duration: 20, // Default 20 seconds (will be adjusted to total 60s for 3 segments)
      image: ''
    };
    setSections([...sections, newSection]);
  };

  const updateSection = (id: string, field: string, value: any) => {
    setSections(sections.map(section => 
      section.id === id ? { ...section, [field]: value } : section
    ));
  };

  const removeSection = (id: string) => {
    setSections(sections.filter(section => section.id !== id));
  };

  const handleSave = async () => {
    console.log('🔵 handleSave called - timerType:', timerType);
    
    // Guard: block submission if hubId is missing
    if (!hubId) {
      console.warn('[TimerForm] Timer creation blocked: no hubId');
      return;
    }
    
    // Guard: rectangular timers must have at least one section
    if (timerType === 'rectangular' && sections.length === 0) {
      toast({
        title: "Missing Sections",
        description: "Please add at least one section to your rectangular timer.",
        variant: "destructive",
      });
      return;
    }
    
    // Calculate duration in seconds for circular timers
    const durationSeconds = timerMode === 'target' 
      ? calculateDurationFromTarget()
      : minutes * 60 + seconds;

    // ── Image upload ────────────────────────────────────────────────────────
    // Any base64 data URL in the timer JSON body exceeds GCP Cloud Armor's
    // body-inspection limit and causes a 403.  Two sources of base64 must be
    // handled here:
    //   A) pendingImageFile.current — a new local file the user just picked
    //   B) imageUrl already containing data: — a base64 stored in the DB from
    //      a save that predates this fix (no new file selected, old blob persists)
    let resolvedImageUrl: string | null = imageUrl || null;

    console.log('[TimerForm] pre-upload check — pendingImageFile:', !!pendingImageFile.current,
      '| imageUrl prefix:', resolvedImageUrl?.substring(0, 40));

    const needsUpload = !!pendingImageFile.current || resolvedImageUrl?.startsWith('data:');

    if (needsUpload) {
      setIsUploading(true);
      try {
        const { getAccessToken } = await import('@/contexts/AuthClientContext');
        const token = await getAccessToken();
        const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

        let fileToUpload: File;
        if (pendingImageFile.current) {
          // Case A: new file picked by user
          fileToUpload = pendingImageFile.current;
          console.log('[TimerForm] uploading pending File:', fileToUpload.name, fileToUpload.size, 'bytes');
        } else {
          // Case B: existing base64 stored in DB — convert to File
          console.log('[TimerForm] imageUrl is base64 — fetching and converting to File for upload');
          const blobRes = await fetch(resolvedImageUrl!);
          const blob = await blobRes.blob();
          const ext = blob.type.split('/')[1] || 'jpg';
          fileToUpload = new File([blob], `timer-image.${ext}`, { type: blob.type });
          console.log('[TimerForm] converted base64 to File:', fileToUpload.name, fileToUpload.size, 'bytes');
        }

        const formData = new FormData();
        formData.append('file', fileToUpload);
        const uploadRes = await fetch('/api/uploads/images', {
          method: 'POST',
          body: formData,
          headers,
          credentials: 'include',
        });
        if (!uploadRes.ok) {
          const errText = await uploadRes.text().catch(() => uploadRes.statusText);
          throw new Error(`Image upload failed (${uploadRes.status}): ${errText}`);
        }
        const uploadData = await uploadRes.json();
        resolvedImageUrl = uploadData.url;
        pendingImageFile.current = null;
        console.log('[TimerForm] upload complete — server url:', resolvedImageUrl);
      } catch (err: any) {
        setIsUploading(false);
        toast({ title: "Image upload failed", description: err.message, variant: "destructive" });
        return;
      }
      setIsUploading(false);
    }

    // Safety net — should never fire if the above logic is correct
    if (resolvedImageUrl?.startsWith('data:')) {
      console.error('[TimerForm] BUG: resolvedImageUrl is still base64 after upload block — clearing to prevent WAF 403');
      resolvedImageUrl = null;
    }
    // ────────────────────────────────────────────────────────────────────────
    
    // Build payload with type included
    const payload: any = {
      name: title,
      type: timerType,
    };
    
    if (timerType === 'rectangular') {
      // For rectangular timers, include segments
      payload.segments = sections.map(section => ({
        id: section.id,
        name: section.title,
        durationSeconds: section.duration,
        image: section.image || null,
      }));
      // Total duration is sum of all segments
      payload.durationSeconds = sections.reduce((sum, s) => sum + s.duration, 0);
      payload.image = null; // Rectangular timers use section images
    } else {
      // Circular timer
      payload.durationSeconds = durationSeconds;
      payload.image = resolvedImageUrl;
    }
    
    payload.completionSound = completionSound || null;
    payload.completionResourceType = completionResourceType || null;
    payload.completionResourceId = completionResourceId || null;
    
    console.log('[TimerForm] payload:', { ...payload, image: payload.image?.substring?.(0, 80) ?? payload.image });
    
    onSave(payload);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[85vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {timer ? 'Edit Timer' : 'Create New Timer'}
        </h2>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Timer Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter timer name..."
            />
          </div>
          
          {timerType === 'circular' && (
            <div>
              <Label>Timer Type</Label>
              <div className="flex gap-4 mt-2 mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="duration"
                    checked={timerMode === 'duration'}
                    onChange={(e) => setTimerMode('duration')}
                  />
                  <span>Set Duration</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    value="target"
                    checked={timerMode === 'target'}
                    onChange={(e) => setTimerMode('target')}
                  />
                  <span>Until a Specific Time</span>
                </label>
              </div>
              
              {timerMode === 'duration' ? (
                <div className="flex items-center space-x-2">
                  <Input
                    type="number"
                    value={minutes}
                    onChange={(e) => setMinutes(parseInt(e.target.value) || 0)}
                    min="0"
                    max="60"
                    className="w-20"
                  />
                  <span>minutes</span>
                  <Input
                    type="number"
                    value={seconds}
                    onChange={(e) => setSeconds(parseInt(e.target.value) || 0)}
                    min="0"
                    max="59"
                    className="w-20"
                  />
                  <span>seconds</span>
                </div>
              ) : (
                <div>
                  <Label htmlFor="target-time">Target Time</Label>
                  <Input
                    id="target-time"
                    type="time"
                    value={targetTime}
                    onChange={(e) => setTargetTime(e.target.value)}
                    className="w-32 mt-1"
                  />
                  <p className="text-sm text-gray-600 mt-1">
                    Timer will count down to this time {targetTime && new Date().getHours() > parseInt(targetTime.split(':')[0]) ? 'tomorrow' : 'today'}
                  </p>
                </div>
              )}
            </div>
          )}

          {timerType === 'rectangular' && (
            <div>
              <Label>Timer Sections</Label>
              <p className="text-sm text-gray-600 mb-4">Add sections that will play sequentially</p>
              
              {sections.length === 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 text-sm">
                    Click "Add Section" to create your first step (like "Apply toothpaste - 20 seconds")
                  </p>
                </div>
              )}
              
              {sections.map((section, index) => (
                <div key={section.id} className="border rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium">Section {index + 1}</h4>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeSection(section.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Remove
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor={`section-title-${section.id}`}>Title</Label>
                      <Input
                        id={`section-title-${section.id}`}
                        value={section.title}
                        onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                        placeholder="Step name..."
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor={`section-duration-${section.id}`}>Duration (mm:ss)</Label>
                      <div className="flex items-center space-x-2">
                        <Input
                          type="number"
                          value={Math.floor(section.duration / 60)}
                          onChange={(e) => {
                            const minutes = parseInt(e.target.value) || 0;
                            const seconds = section.duration % 60;
                            updateSection(section.id, 'duration', minutes * 60 + seconds);
                          }}
                          min="0"
                          max="59"
                          placeholder="0"
                          className="w-16"
                        />
                        <span>:</span>
                        <Input
                          type="number"
                          value={section.duration % 60}
                          onChange={(e) => {
                            const minutes = Math.floor(section.duration / 60);
                            const seconds = parseInt(e.target.value) || 0;
                            updateSection(section.id, 'duration', minutes * 60 + seconds);
                          }}
                          min="0"
                          max="59"
                          placeholder="00"
                          className="w-16"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <Label>Section Image</Label>
                    <div className="flex gap-2 mt-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = (e) => {
                                updateSection(section.id, 'image', e.target?.result as string);
                              };
                              reader.readAsDataURL(file);
                            }
                          };
                          input.click();
                        }}
                      >
                        Upload Photo
                      </Button>
                      {section.image && (
                        <img 
                          src={section.image} 
                          alt={section.title}
                          className="w-12 h-12 object-cover rounded"
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              <Button
                type="button"
                variant="outline"
                onClick={addSection}
                className="w-full mb-4"
              >
                Add Section
              </Button>
            </div>
          )}

          {timerType === 'circular' && (
            <div>
              <Label>Timer Image</Label>
              <div className="mt-2">
                <ImageSelector
                  onImageSelected={(result) => {
                    if (!result) {
                      setImageUrl('');
                      pendingImageFile.current = null;
                      return;
                    }
                    if (result.type === 'upload') {
                      // Store the File for deferred upload; use the base64 preview only for display.
                      // The actual upload happens in handleSave to keep the timer JSON body small.
                      pendingImageFile.current = result.file;
                      setImageUrl(result.preview);
                    } else {
                      pendingImageFile.current = null;
                      setImageUrl(result.url);
                    }
                  }}
                  currentImageUrl={imageUrl || null}
                />
              </div>
            </div>
          )}
          
          <div className="border-t pt-4 mt-2">
            <Label className="text-base font-semibold mb-3 block">When Timer Ends</Label>
            
            <div className="space-y-3">
              <div>
                <Label className="text-sm text-muted-foreground">Completion Sound</Label>
                <select
                  className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={completionSound}
                  onChange={(e) => setCompletionSound(e.target.value)}
                >
                  <option value="chime">Chime</option>
                  <option value="bell">Bell</option>
                  <option value="celebration">Celebration</option>
                  <option value="gentle">Gentle Tone</option>
                  <option value="none">No Sound</option>
                </select>
              </div>

              <div>
                <Label className="text-sm text-muted-foreground">Open Resource After Completion</Label>
                <select
                  className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={completionResourceType ? `${completionResourceType}:${completionResourceId}` : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val) {
                      setCompletionResourceType('');
                      setCompletionResourceId('');
                    } else {
                      const [type, ...idParts] = val.split(':');
                      setCompletionResourceType(type);
                      setCompletionResourceId(idParts.join(':'));
                    }
                  }}
                >
                  <option value="">None</option>
                  {availableResources?.map((r) => (
                    <option key={`${r.type}:${r.id}`} value={`${r.type}:${r.id}`}>
                      {r.type === 'schedule' ? '📋' : '📖'} {r.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={onCancel} disabled={isUploading}>
              Cancel
            </Button>
            <Button
              onClick={() => { handleSave(); }}
              disabled={!title.trim() || isUploading}
            >
              {isUploading ? 'Uploading image…' : (timer ? 'Update Timer' : 'Create Timer')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}